import { z } from "zod";
import { COURSE_IDS } from "./courses.js";
import { STUDENT_STATUSES, BATCH_STATUSES, IDPH_STATUSES, PAYMENT_STATUSES, EXAM_RESULTS, } from "./enums.js";
/**
 * Payment status is derived from the money, never set by hand:
 *   nothing received -> Pending; some received with a balance left -> Partial;
 *   balance cleared (due <= 0) -> Paid. When the balance is unknown (no fee on
 *   file and no due entered) a received amount can't be confirmed as paid-in-full,
 *   so it stays Partial. Shared so the server (authoritative) and the admin form
 *   (live preview) agree.
 */
export function derivePaymentStatus(received, due) {
    if (!(received > 0))
        return "Pending";
    if (due != null && due <= 0)
        return "Paid";
    return "Partial";
}
/**
 * Phase 1b contracts: batches, enrollment, and the student lifecycle record
 * (with embedded IDPH + payment). Validated server-side in the adminApi Lambda;
 * the admin React app imports the inferred types.
 *
 * IDPH is course-conditional: it lives on the student only when the student's
 * course has `idphRequired === true`. That gating is enforced in the handler
 * (the schema doesn't know the student's course) - see students.ts.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
const money = z.number().nonnegative().max(1_000_000);
/** Batch size: max 100 students/batch (scope constraint); default to the max. */
export const MAX_BATCH_CAPACITY = 100;
export const DEFAULT_BATCH_CAPACITY = 100;
// ---- Batches ----
export const batchInputSchema = z
    .object({
    courseId: z.enum(COURSE_IDS, { errorMap: () => ({ message: "Choose a program" }) }),
    name: z.string().trim().min(1, "Name is required").max(80),
    startDate: isoDate,
    endDate: isoDate,
    capacity: z.number().int().positive().max(MAX_BATCH_CAPACITY).default(DEFAULT_BATCH_CAPACITY),
    status: z.enum(BATCH_STATUSES).default("Upcoming"),
})
    .refine((b) => b.endDate >= b.startDate, {
    message: "End date must be on or after the start date",
    path: ["endDate"],
});
/** Partial update: any subset of the editable batch fields. */
export const batchUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    startDate: isoDate.optional(),
    endDate: isoDate.optional(),
    capacity: z.number().int().positive().max(MAX_BATCH_CAPACITY).optional(),
    status: z.enum(BATCH_STATUSES).optional(),
})
    .refine((b) => Object.keys(b).length > 0, { message: "Nothing to update" });
export const batchSchema = z.object({
    batchId: z.string().uuid(),
    courseId: z.enum(COURSE_IDS),
    name: z.string(),
    startDate: isoDate,
    endDate: isoDate,
    capacity: z.number().int().positive(),
    status: z.enum(BATCH_STATUSES),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdBy: z.string(),
});
// ---- Enrollment (from a prospect) ----
/** Student identity is copied server-side from the prospect; only the batch is chosen. */
export const enrollInputSchema = z.object({
    batchId: z.string().uuid("Choose a batch"),
});
// ---- Student lifecycle + embedded sub-objects ----
export const studentStatusUpdateSchema = z.object({
    status: z.enum(STUDENT_STATUSES),
});
export const idphUpdateSchema = z.object({
    status: z.enum(IDPH_STATUSES),
    notes: z.string().trim().max(2000).optional(),
});
export const examAttemptSchema = z.object({
    result: z.enum(EXAM_RESULTS),
    date: isoDate,
});
export const paymentUpdateSchema = z.object({
    /** Optional/ignored - status is derived server-side from the amounts. */
    status: z.enum(PAYMENT_STATUSES).optional(),
    amountDue: money.optional(),
    /** Absolute cumulative total (legacy / corrections). */
    amountPaid: money.optional(),
    /** Amount received in THIS transaction; the server adds it to the running total. */
    paymentAmount: money.optional(),
    notes: z.string().trim().max(2000).optional(),
});
const examAttempt = z.object({
    attemptNumber: z.number().int().min(1),
    result: z.enum(EXAM_RESULTS),
    date: isoDate,
});
const idphRecord = z.object({
    status: z.enum(IDPH_STATUSES),
    attempts: z.array(examAttempt).default([]),
    notes: z.string().optional(),
    updatedAt: z.string().datetime().optional(),
    updatedBy: z.string().optional(),
});
/** One owner-made payment change, snapshotted for the activity log. */
const paymentLogEntry = z.object({
    at: z.string().datetime(),
    by: z.string(),
    status: z.enum(PAYMENT_STATUSES),
    /** Amount received in this specific transaction (the installment). */
    paidNow: money.optional(),
    /** Running totals after this transaction. */
    amountPaid: money.optional(),
    amountDue: money.optional(),
    note: z.string().optional(),
});
const paymentRecord = z.object({
    status: z.enum(PAYMENT_STATUSES),
    amountDue: money.optional(),
    amountPaid: money.optional(),
    notes: z.string().optional(),
    /** Append-only audit trail of every payment save (newest appended last). */
    history: z.array(paymentLogEntry).default([]),
    updatedAt: z.string().datetime().optional(),
    updatedBy: z.string().optional(),
});
export const studentSchema = z.object({
    studentId: z.string().uuid(),
    prospectId: z.string().uuid().optional(),
    batchId: z.string().uuid(),
    courseId: z.enum(COURSE_IDS),
    /** Course fee (USD) snapshotted at enrollment; absent if the course has none set. */
    feeUsd: z.number().nonnegative().optional(),
    fullName: z.string(),
    email: z.string().email(),
    phone: z.string().optional(),
    mailingAddress: z.string().optional(),
    status: z.enum(STUDENT_STATUSES),
    enrolledAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    createdBy: z.string(),
    /** Set by the batch-access sweep when the portal login was disabled
     * (30 days after the batch completed, no other active enrollment). */
    accessRevokedAt: z.string().datetime().optional(),
    idph: idphRecord.optional(),
    payment: paymentRecord,
});
//# sourceMappingURL=student.js.map